import React, { useEffect, useState } from 'react';

import { useConsentTodos, useNextInstructions } from './MedicationsWidget.hooks';
import {
  CollapsibleDayContainer,
  CollapsibleDayTitleContainer,
  DaysAndLastUpdateContainer,
  DaysContainer,
  GoForwardArrow,
  GoUpArrow,
  InstructionDay,
  LastUpdateContainer,
  LastUpdateIconContainer,
  LastUpdateTextContainer,
  MedicationInstructionsWrapper,
  MedicationsWidgetContainer,
  MedicationsWidgetTitleContainer,
  MobileButtonContainer
} from './MedicationsWidget.styled';
import { WidgetTitleContainer } from '../Widgets/Widgets.styled';
import { ArrowLeft, ArrowRight, CheckCircle } from 'assets/icons/Icons';
import { Button } from 'components/v2/Buttons/Button/Button';
import { Collapsible } from 'components/v2/Collapsible';
import { GenericDropdown } from 'components/v2/DropdownMenu/Dropdown';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import { Patient, themes } from 'kb-shared';
import { momentFormatted } from 'kb-shared/utilities/momentHelpers';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { IntercourseReminder } from './IntercourseReminder/IntercourseReminder';
import { MedicationsAndNotes } from './MedicationsAndNotes/MedicationsAndNotes';
import { medicationInstructionsMock } from './MedicationsWidget.constants';
import { MedicationInstruction } from './MedicationsWidget.types';
import {
  areInstructionsMissing,
  getFormattedDateLabel,
  getFormattedTitleLabel,
  getInstructionForMobile,
  getInstructionsAfter,
  getInstructionsBefore,
  getLongFormattedDateLabel,
  getOpenInstructions
} from './MedicationsWidget.utils';
import { MedicationsWidgetBlocker } from './MedicationsWidgetBlocker/MedicationsWidgetBlocker';
import { MedicationsWidgetRetry } from './MedicationsWidgetRetry/MedicationsWidgetRetry';

export const MedicationsWidget = ({ patient }: { patient: Patient }) => {
  const {
    data: instructionsData,
    error: instructionsError,
    loading: instructionsLoading,
    refetch: refetchInstructions
  } = useNextInstructions();
  const { data: consentTodos, loading: consentTodosLoading } = useConsentTodos();
  const { isMobile } = useBreakpoints();
  const [selectedInstructionIndex, setSelectedInstructionIndex] = useState<number>(0);
  const [showAllInstructionsOnMobile, setShowAllInstructionsOnMobile] = useState<boolean>(false);
  const hasPendingConsentTodos = consentTodos.length > 0;
  const blockMedicationInstructions = Boolean(hasPendingConsentTodos);

  useEffect(() => {
    // reset to the first instruction(initial state) when resizing window from small to desktop screen
    // otherwise, the selected instruction index could be -1 and no instruction will be displayed
    if (!isMobile) {
      setSelectedInstructionIndex(0);
      setShowAllInstructionsOnMobile(false);
    }
  }, [isMobile]);

  if (instructionsError) return <MedicationsWidgetRetry onRetry={() => refetchInstructions()} />;

  if (
    !instructionsData ||
    areInstructionsMissing(instructionsData) ||
    instructionsLoading ||
    consentTodosLoading
  ) {
    return null;
  }

  const instructions = instructionsData.nextInstructions;
  const items: Array<MedicationInstruction> = instructions.map((instruction, index) => ({
    index,
    titleLabel: getFormattedTitleLabel(instruction),
    dateLabel: isMobile
      ? getLongFormattedDateLabel(instruction)
      : getFormattedDateLabel(instruction),
    data: instruction
  }));
  // when medication instructions are blocked with overlay, we show mock data so that it's non-obvious how to get or act upon real medication instruction data
  // even if the user tampers with DOM and/or removes elements/styling
  const shownItems: Array<MedicationInstruction> = blockMedicationInstructions
    ? medicationInstructionsMock
    : items;

  const firstDay = instructions[0];
  const hasIntercourseReminder = 'haveIntercourse' in firstDay && firstDay.haveIntercourse;
  const medicationsExist = shownItems.find(item => item.data.drugs?.length > 0) !== null;
  const instructionsExist = shownItems.find(item => item.data.instructions) !== null;

  const instructionsToDisplay = isMobile
    ? getInstructionForMobile(
        shownItems,
        showAllInstructionsOnMobile || blockMedicationInstructions
      )
    : getOpenInstructions(shownItems, selectedInstructionIndex);

  const showPreviousInstructionsButton = shownItems.length > 3 && selectedInstructionIndex > 1;
  const showNextInstructionsButton =
    shownItems.length > 3 && selectedInstructionIndex < shownItems.length - 2;
  const showMedicationInstructionContent = selectedInstructionIndex > -1;

  // Normalize the selected instruction index to 0, 1, or 2 in order for the selected instruction to be displayed
  // in the middle and that index is in the range of the length of the shown items. This is applied only for desktop view.
  let normalizedInstructionIndex = 0;

  if (selectedInstructionIndex === 0) {
    normalizedInstructionIndex = 0;
  } else if (shownItems.length > 2 && selectedInstructionIndex + 1 === shownItems.length) {
    normalizedInstructionIndex = 2;
  } else {
    normalizedInstructionIndex = 1;
  }

  return (
    <>
      {hasIntercourseReminder && firstDay && <IntercourseReminder date={firstDay.date} />}
      {(medicationsExist || instructionsExist) && (
        <MedicationsWidgetContainer>
          <MedicationInstructionsWrapper>
            {blockMedicationInstructions && <MedicationsWidgetBlocker />}
            <MedicationsWidgetTitleContainer>
              <WidgetTitleContainer $noMargin>
                <Heading tag="div" styledAs="h2">
                  Medications and Instructions
                </Heading>
              </WidgetTitleContainer>
            </MedicationsWidgetTitleContainer>
            {!isMobile && (
              <>
                <DaysAndLastUpdateContainer>
                  <DaysContainer>
                    {showPreviousInstructionsButton && (
                      <GenericDropdown
                        items={getInstructionsBefore(
                          shownItems,
                          instructionsToDisplay,
                          setSelectedInstructionIndex
                        )}
                        className="dropdown-menu-modal open-modal-bottom-left"
                      >
                        <Button
                          className="dropdown-open-button"
                          category="secondary"
                          leftIcon={<ArrowLeft type="solid" />}
                        />
                      </GenericDropdown>
                    )}
                    {instructionsToDisplay.map(item => {
                      const isActive = selectedInstructionIndex === item.index;

                      return (
                        <InstructionDay
                          $active={isActive}
                          onClick={() => setSelectedInstructionIndex(item.index)}
                          key={item.data.id}
                        >
                          <Text fontStyle={isActive ? 'bold' : 'regular'}>{item.titleLabel}</Text>
                          <Text color={themes.colors.neutral.lightNavy} size="xs">
                            {item.dateLabel}
                          </Text>
                        </InstructionDay>
                      );
                    })}
                    {showNextInstructionsButton && (
                      <GenericDropdown
                        items={getInstructionsAfter(
                          shownItems,
                          instructionsToDisplay,
                          setSelectedInstructionIndex
                        )}
                        className="dropdown-menu-modal"
                      >
                        <Button
                          className="dropdown-open-button"
                          category="secondary"
                          leftIcon={<ArrowRight type="solid" />}
                        />
                      </GenericDropdown>
                    )}
                  </DaysContainer>
                  {showMedicationInstructionContent && (
                    <LastUpdateContainer>
                      <LastUpdateTextContainer>
                        <Text size="sm" fontStyle="medium" color={themes.colors.green.darkestGreen}>
                          Last updated
                        </Text>
                        <Text size="sm" fontStyle="medium" color={themes.colors.green.darkestGreen}>
                          {momentFormatted(
                            instructionsToDisplay[normalizedInstructionIndex].data.updatedAt,
                            'M/D/YY h:mm A zz'
                          )}
                        </Text>
                      </LastUpdateTextContainer>
                      <LastUpdateIconContainer>
                        <CheckCircle type="solid" />
                      </LastUpdateIconContainer>
                    </LastUpdateContainer>
                  )}
                </DaysAndLastUpdateContainer>
                {showMedicationInstructionContent && (
                  <MedicationsAndNotes
                    medicationInstruction={instructionsToDisplay[normalizedInstructionIndex].data}
                    patient={patient}
                    hideAcknowledgment={
                      blockMedicationInstructions ||
                      !instructionsToDisplay[normalizedInstructionIndex].data.started
                    }
                  />
                )}
              </>
            )}
            {isMobile && (
              <>
                {instructionsToDisplay.map(item => {
                  const isActive = selectedInstructionIndex === item.index;
                  return (
                    <CollapsibleDayContainer $active={isActive} key={item.data.id}>
                      <CollapsibleDayTitleContainer
                        onClick={() =>
                          item.index !== selectedInstructionIndex
                            ? setSelectedInstructionIndex(item.index)
                            : setSelectedInstructionIndex(-1)
                        }
                      >
                        <div>
                          <div>
                            <Text fontStyle="bold">{item.titleLabel}</Text>
                          </div>
                          <div>
                            <Text size="xs">{item.dateLabel}</Text>
                          </div>
                        </div>
                        {isActive ? <GoUpArrow /> : <GoForwardArrow />}
                      </CollapsibleDayTitleContainer>
                      {showMedicationInstructionContent && (
                        <Collapsible visible={isActive}>
                          <LastUpdateContainer>
                            <LastUpdateTextContainer>
                              <Text
                                size="sm"
                                fontStyle="medium"
                                color={themes.colors.green.darkestGreen}
                              >
                                Updated: {momentFormatted(item.data.updatedAt, 'M/D/YY h:mm A zz')}
                              </Text>
                            </LastUpdateTextContainer>
                            <LastUpdateIconContainer>
                              <CheckCircle type="solid" />
                            </LastUpdateIconContainer>
                          </LastUpdateContainer>
                          <MedicationsAndNotes
                            medicationInstruction={item.data}
                            patient={patient}
                            hideAcknowledgment={blockMedicationInstructions || !item.data.started}
                          />
                        </Collapsible>
                      )}
                    </CollapsibleDayContainer>
                  );
                })}
                <MobileButtonContainer>
                  <Button
                    label={
                      showAllInstructionsOnMobile ? 'Hide All Medications' : 'Show All Medications'
                    }
                    category="primary"
                    size="lg"
                    type="button"
                    onClick={() => setShowAllInstructionsOnMobile(!showAllInstructionsOnMobile)}
                  />
                </MobileButtonContainer>
              </>
            )}
          </MedicationInstructionsWrapper>
        </MedicationsWidgetContainer>
      )}
    </>
  );
};
